import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Container,
  Alert,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Fade,
  Divider,
  Stack,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd,
  LockReset,
  Event,
  Info,
  ArrowBack,
  Email,
  Security,
  CheckCircle,
  Error,
  Pets,
  DirectionsRun,
  Groups,
  CalendarToday,
  Receipt,
  TrendingUp,
  ManageAccounts,
  EmojiEvents,
  ChevronRight,
} from "@mui/icons-material";
import Logo from "../../EasyAgility.png";
import axios from "axios";
import Cookies from "universal-cookie";
import { Link as RouterLink } from "react-router-dom";
import { add } from "date-fns";
import Register from "../Register/component";
import Notify from "../Notify/component";
import PasswordStrengthBar from "react-password-strength-bar";
import {
  createClubRedirectPath,
  hasCreateClubIntent,
} from "../../utils/createClub";
import { featureFlags } from "../../utils/featureFlags";
import CreateClubAuthPrompt from "../AgilityClubs/CreateClubAuthPrompt";
import CreateClubPrompt from "../AgilityClubs/CreateClubPrompt";
import MailingListSection from "../Entries/MailingListSection";

export type jwtCookie = {
  expires: Date;
  path: string;
  sameSite: "strict";
  secure?: boolean;
};

const Login = ({
  setLoggedIn,
}: {
  setLoggedIn: (loggedIn: boolean) => void;
}): JSX.Element => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [register, setRegister] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<"error" | "success">("error");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [passwordScore, setPasswordScore] = useState(0);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Decode JWT to check for temporary_password
  const decodeJWT = (token: string): any => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.error("Error decoding JWT:", err);
      return null;
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setMessage("Please enter both email and password");
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `https://identity.easyagility.co.uk/token?scope=all&username=${email}&password=${encodeURIComponent(
          password,
        )}&grant_type=password`,
        {},
        {
          headers: {
            Authorization: "Basic Mzc1OTozNzQ4MjEwMjM=",
          },
        },
      );
      const expiryTime: Date = add(new Date(), {
        seconds: response.data.expires_in,
      });
      const cookieOptions: jwtCookie = {
        expires: expiryTime,
        path: "/",
        sameSite: "strict" as "strict",
        secure:
          window.location.origin.indexOf("easyagility") !== -1 || undefined,
      };
      // Check if temporary_password is set to true BEFORE saving to cookies
      const decodedToken = decodeJWT(response.data.access_token);
      console.log("Decoded token:", decodedToken);
      const hasTemporaryPassword =
        decodedToken?.user?.temporary_password === true;
      console.log("Has temporary password:", hasTemporaryPassword);
      if (hasTemporaryPassword) {
        console.log(
          "Temporary password detected, opening change password dialog",
        );
        // Store token temporarily in state (not cookies) so App doesn't think we're logged in
        setTempToken(response.data.access_token);
        setLoading(false);
        setChangePasswordOpen(true);
        return;
      }

      // Only save to cookies if NOT a temporary password
      const cookies = new Cookies();
      cookies.set("token", response.data.access_token, cookieOptions);

      const urlParams = new URLSearchParams(window.location.search);
      const authenticateResults = urlParams.get("authenticate-results");

      if (authenticateResults === "true") {
        window.location.href = `https://results.easyagility.co.uk?token=${response.data.access_token}`;
      }

      const authenticateEntries = urlParams.get("authenticate-entries");
      if (authenticateEntries === "true") {
        window.location.href = `https://entries.easyagility.co.uk?token=${response.data.access_token}`;
        return;
      }

      setLoggedIn(true);
      window.location.href = hasCreateClubIntent(window.location.search)
        ? createClubRedirectPath()
        : "/";
    } catch (err) {
      console.log(err);
      setMessage(
        "Username or password incorrect, please try again or register an account. If the issue continues please contact Matt at matt@easyagility.co.uk",
      );
      setSeverity("error");
      setNotifyOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const validateEmail = (email: string): RegExpMatchArray | null => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      setMessage("Please enter both new password and confirmation");
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    if (passwordScore < 2) {
      setMessage("Password must be at least 'Good' strength");
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setMessage("Passwords do not match");
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    try {
      setChangePasswordLoading(true);
      // Use tempToken if available, otherwise get from cookies
      const token = tempToken || new Cookies().get("token");

      // The password used to log in is the current password. The email
      // reset link also puts it in the URL, and that value is copied into
      // state on load, so the typed value covers both paths.
      const queryParams = new URLSearchParams(window.location.search);
      const currentPassword =
        password || queryParams.get("password") || "";

      // Call API to change password
      await axios.post(
        `https://identity.easyagility.co.uk/change-password`,
        {
          new_password: newPassword,
          current_password: currentPassword,
          email_address: email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // After password change, re-login to get new token without temporary_password
      const loginResponse = await axios.post(
        `https://identity.easyagility.co.uk/token?scope=all&username=${email}&password=${encodeURIComponent(
          newPassword,
        )}&grant_type=password`,
        {},
        {
          headers: {
            Authorization: "Basic Mzc1OTozNzQ4MjEwMjM=",
          },
        },
      );

      const expiryTime: Date = add(new Date(), {
        seconds: loginResponse.data.expires_in,
      });
      const cookieOptions: jwtCookie = {
        expires: expiryTime,
        path: "/",
        sameSite: "strict" as "strict",
        secure:
          window.location.origin.indexOf("easyagility") !== -1 || undefined,
      };
      const cookies = new Cookies();
      cookies.set("token", loginResponse.data.access_token, cookieOptions);
      setTempToken(null); // Clear temporary token

      setChangePasswordOpen(false);
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordScore(0);
      setPassword(newPassword);

      const urlParams = new URLSearchParams(window.location.search);
      const authenticateResults = urlParams.get("authenticate-results");

      if (authenticateResults === "true") {
        window.location.href = `https://results.easyagility.co.uk?token=${loginResponse.data.access_token}`;
        return;
      }

      const authenticateEntries = urlParams.get("authenticate-entries");
      if (authenticateEntries === "true") {
        window.location.href = `https://entries.easyagility.co.uk?token=${loginResponse.data.access_token}`;
        return;
      }

      setLoggedIn(true);
      window.location.href = hasCreateClubIntent(window.location.search)
        ? createClubRedirectPath()
        : "/";
    } catch (err) {
      console.log(err);
      setMessage(
        "There was an error changing your password. Please try again or contact matt@easyagility.co.uk if the problem persists.",
      );
      setSeverity("error");
      setNotifyOpen(true);
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setMessage("Please enter your email address");
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    if (!validateEmail(resetEmail)) {
      setMessage("Please enter a valid email address");
      setSeverity("error");
      setNotifyOpen(true);
      return;
    }

    try {
      setResetLoading(true);
      await axios.post(
        `https://identity.easyagility.co.uk/forgotten-password`,
        {
          email_address: resetEmail,
        },
        {
          headers: {
            Authorization: "Basic Mzc1OTozNzQ4MjEwMjM=",
          },
        },
      );
      setMessage(
        "If an account exists with that email address, a password reset link has been sent. Please check your email.",
      );
      setSeverity("success");
      setNotifyOpen(true);
      setForgotPassword(false);
      setResetEmail("");
    } catch (err) {
      console.log(err);
      setMessage(
        "There was an error sending the reset email. Please try again or contact matt@easyagility.co.uk if the problem persists.",
      );
      setSeverity("error");
      setNotifyOpen(true);
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        if (forgotPassword) {
          handleResetPassword();
        } else {
          handleLogin();
        }
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
    // eslint-disable-next-line
  }, [email, password, forgotPassword, resetEmail]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    const passwordParam = urlParams.get("password");

    if (emailParam) {
      setEmail(emailParam);
    }
    if (passwordParam) {
      setPassword(passwordParam);
    }
    // If both email and password are provided, show the password by default
    if (emailParam && passwordParam) {
      setShowPassword(true);
    }
  }, []);

  if (register) {
    return (
      <Register setRegister={setRegister} setLoggedIn={setLoggedIn} />
    );
  }

  if (forgotPassword) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
          display: "flex",
          alignItems: "center",
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Fade in timeout={600}>
            <Paper
              elevation={8}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <IconButton
                  onClick={() => {
                    setForgotPassword(false);
                    setResetEmail("");
                  }}
                  sx={{
                    mr: 2,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.light + "20",
                    },
                  }}
                  aria-label="Go back to login"
                >
                  <ArrowBack />
                </IconButton>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Reset Password
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 4,
                }}
              >
                Enter your email address and we'll send you a link to reset your
                password.
              </Typography>

              <Stack spacing={3}>
                <TextField
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  label="Email Address"
                  fullWidth
                  variant="outlined"
                  type="email"
                  autoComplete="email"
                  error={resetEmail.length > 0 && !validateEmail(resetEmail)}
                  helperText={
                    resetEmail.length > 0 && !validateEmail(resetEmail)
                      ? "Please enter a valid email address"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleResetPassword}
                  disabled={
                    resetLoading || !resetEmail || !validateEmail(resetEmail)
                  }
                  startIcon={<LockReset />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  {resetLoading ? "Sending..." : "Send Reset Link"}
                </Button>

                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    or
                  </Typography>
                </Divider>

                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={() => {
                    setForgotPassword(false);
                    setResetEmail("");
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    py: 1.5,
                  }}
                >
                  Back to Sign In
                </Button>
              </Stack>
            </Paper>
          </Fade>
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              <Link
                component={RouterLink}
                to="/privacy-policy"
                underline="hover"
              >
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </Container>

        <Notify
          open={notifyOpen}
          setOpen={setNotifyOpen}
          message={message}
          severity={severity}
        />
      </Box>
    );
  }

  const createClubIntent = hasCreateClubIntent(window.location.search);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
        display: "flex",
        alignItems: "flex-start",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        {featureFlags.clubAdministration && !createClubIntent && (
          <CreateClubPrompt />
        )}

        <Grid container spacing={4} alignItems="center">
          {/* Left Side - Feature showcase */}
          {!createClubIntent && (
          <Grid item xs={12} md={6}>
            <Fade in timeout={600}>
              <Box sx={{ mb: { xs: 4, md: 0 }, pr: { md: 3 } }}>
                {/* Logo + brand */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      bgcolor: "#00395A",
                      borderRadius: 2,
                      p: "8px 10px",
                      display: "flex",
                      alignItems: "center",
                      boxShadow: "0 2px 8px rgba(0,57,90,0.25)",
                    }}
                  >
                    <img
                      src={Logo}
                      alt="Easy Agility"
                      style={{ height: 36, width: "auto" }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "#00395A",
                        lineHeight: 1.1,
                      }}
                    >
                      Easy Agility
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Hub
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    color: "#00395A",
                    mb: 1.5,
                    fontSize: { xs: "1.9rem", md: "2.4rem" },
                    lineHeight: 1.15,
                  }}
                >
                  Everything your club needs, in one place
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    mb: 4,
                    fontSize: "1.05rem",
                    lineHeight: 1.7,
                  }}
                >
                  From entering shows to managing club training groups and
                  invoicing - Easy Agility keeps the admin side simple so you
                  can focus on the fun part.
                </Typography>

                {/* Feature list */}
                <Stack spacing={2} sx={{ mb: 4 }}>
                  {[
                    {
                      icon: <Event />,
                      title: "Show diary and entries",
                      desc: "Browse upcoming shows and enter with a few taps.",
                    },
                    {
                      icon: <TrendingUp />,
                      title: "Results and history",
                      desc: "View your results from past shows in one timeline.",
                    },
                    {
                      icon: <Pets />,
                      title: "Dog and handler management",
                      desc: "Keep all your dogs and handlers on your account.",
                    },
                    {
                      icon: <Groups />,
                      title: "Club training groups",
                      desc: "Training schedules, session notes and upcoming dates.",
                    },
                    {
                      icon: <Receipt />,
                      title: "Monthly invoicing",
                      desc: "Clubs can generate and send invoices to members in seconds.",
                    },
                  ].map((f) => (
                    <Box
                      key={f.title}
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 1.5,
                          bgcolor: "#00395A12",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.25,
                          "& .MuiSvgIcon-root": {
                            fontSize: 20,
                            color: "#00395A",
                          },
                        }}
                      >
                        {f.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ mb: 0.25 }}
                        >
                          {f.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {f.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                {/* Pricing teaser */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: "#00395A30",
                    bgcolor: "#00395A06",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: "#00395A" }}
                    >
                      Club administration pricing
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Free for clubs up to 5 members. Plans from £9/month.
                    </Typography>
                  </Box>
                  <Button
                    component={RouterLink}
                    to="/pricing"
                    endIcon={<ChevronRight />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#00395A",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      "&:hover": { bgcolor: "#00395A10" },
                    }}
                  >
                    See pricing
                  </Button>
                </Paper>
              </Box>
            </Fade>
          </Grid>
          )}

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={createClubIntent ? 12 : 6}>
            <Fade in timeout={800}>
              <Paper
                elevation={8}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  maxWidth: 500,
                  mx: "auto",
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1,
                    textAlign: "center",
                  }}
                >
                  {createClubIntent
                    ? "Sign in to create your club"
                    : "Sign In"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: createClubIntent ? 2 : 4,
                    textAlign: "center",
                  }}
                >
                  {createClubIntent
                    ? "Use your existing account, or create a new one below."
                    : "Enter your credentials to access your account"}
                </Typography>

                {createClubIntent && (
                  <CreateClubAuthPrompt />
                )}

                <Stack spacing={3}>
                  {/* Email Field */}
                  <TextField
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    label="Email Address"
                    fullWidth
                    variant="outlined"
                    type="email"
                    autoComplete="email"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Password Field */}
                  <TextField
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    label="Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Login Button */}
                  <Button
                    variant="contained"
                    onClick={handleLogin}
                    disabled={loading}
                    startIcon={<LoginIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {loading ? "Signing In..." : "Sign In"}
                  </Button>

                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      or
                    </Typography>
                  </Divider>

                  {/* Additional Actions */}
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PersonAdd />}
                      onClick={() => setRegister(true)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        py: 1.5,
                      }}
                    >
                      {createClubIntent
                        ? "Create a new account"
                        : "Create New Account"}
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<LockReset />}
                      onClick={() => setForgotPassword(true)}
                      sx={{
                        textTransform: "none",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Forgot Password?
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        {/* Three-panel feature strip */}
        <Box sx={{ mt: { xs: 4, md: 7 }, width: "100%" }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#00395A",
                textAlign: "center",
                mb: 1,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                px: { xs: 1, sm: 0 },
              }}
            >
              Who is Easy Agility for?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: "center",
                mb: { xs: 3, sm: 4 },
                px: { xs: 2, sm: 0 },
              }}
            >
              Whether you compete, train, or run a club — there's something here
              for you.
            </Typography>
            <Grid container spacing={{ xs: 3, sm: 2.5 }} alignItems="stretch">
              {/* Competitors */}
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: theme.shadows[3] },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "#00395A12",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      flexShrink: 0,
                      "& .MuiSvgIcon-root": { fontSize: 26, color: "#00395A" },
                    }}
                  >
                    <EmojiEvents />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Competitors
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: { xs: 2, sm: 2.5 },
                      flex: { xs: "none", sm: 1 },
                      lineHeight: 1.7,
                    }}
                  >
                    Browse the show diary, enter shows, and view your results
                    all in one account.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth={isMobile}
                    endIcon={<ChevronRight />}
                    onClick={() =>
                      handleExternalLink("https://entries.easyagility.co.uk")
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      borderColor: "#00395A",
                      color: "#00395A",
                      fontWeight: 600,
                      alignSelf: "flex-start",
                      "&:hover": { bgcolor: "#00395A0a" },
                    }}
                  >
                    Show diary
                  </Button>
                </Paper>
              </Grid>

              {/* Club admins */}
              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#00395A",
                    backgroundImage:
                      "linear-gradient(135deg, #00395A 0%, #1a5a7a 100%)",
                    boxShadow: {
                      xs: "0 4px 16px rgba(0,57,90,0.2)",
                      sm: "0 6px 24px rgba(0,57,90,0.25)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      "& .MuiSvgIcon-root": { fontSize: 26, color: "white" },
                    }}
                  >
                    <ManageAccounts />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1, color: "white" }}
                  >
                    Club Admins
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      flex: { xs: "none", sm: 1 },
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    Manage members, training groups, session schedules and
                    monthly invoicing — all in one dashboard.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "stretch", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label="Free up to 5 members"
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        alignSelf: { xs: "flex-start", sm: "auto" },
                      }}
                    />
                    <Button
                      component={RouterLink}
                      to="/pricing"
                      endIcon={<ChevronRight />}
                      size="small"
                      fullWidth={isMobile}
                      sx={{
                        textTransform: "none",
                        color: "rgba(255,255,255,0.85)",
                        fontWeight: 600,
                        p: { xs: 1, sm: 0 },
                        minWidth: 0,
                        justifyContent: { xs: "center", sm: "flex-start" },
                        border: {
                          xs: "1px solid rgba(255,255,255,0.35)",
                          sm: "none",
                        },
                        borderRadius: { xs: 2, sm: 0 },
                        "&:hover": {
                          color: "white",
                          bgcolor: {
                            xs: "rgba(255,255,255,0.1)",
                            sm: "transparent",
                          },
                        },
                      }}
                    >
                      See pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* Show organisers */}
              <Grid item xs={12} sm={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: theme.shadows[3] },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "#00395A12",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      flexShrink: 0,
                      "& .MuiSvgIcon-root": { fontSize: 26, color: "#00395A" },
                    }}
                  >
                    <CalendarToday />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Show Organisers
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: { xs: 2, sm: 2.5 },
                      flex: { xs: "none", sm: 1 },
                      lineHeight: 1.7,
                    }}
                  >
                    Running a show? Easy Agility handles entries, results and
                    ring plans. Get in touch for a tailored quote.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth={isMobile}
                    endIcon={<ChevronRight />}
                    href="mailto:matt@easyagility.co.uk?subject=Show processing enquiry"
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      borderColor: "#00395A",
                      color: "#00395A",
                      fontWeight: 600,
                      alignSelf: "flex-start",
                      "&:hover": { bgcolor: "#00395A0a" },
                    }}
                  >
                    Contact Matt
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <MailingListSection variant="login" />
              </Grid>
            </Grid>
        </Box>

        <Box
          sx={{
            mt: { xs: 4, md: 3 },
            pb: { xs: 2, md: 0 },
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/pricing" underline="hover">
              Pricing
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/privacy-policy" underline="hover">
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Container>

      {/* Password Change Dialog */}
      <Dialog
        open={changePasswordOpen}
        onClose={(event, reason) => {
          // Prevent closing the dialog - user must change password
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Security sx={{ color: theme.palette.warning.main }} />
            <Typography variant="h6" component="span">
              Change Your Password
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            You are using a temporary password. Please change it to continue.
          </Alert>
          <Stack spacing={3}>
            {/* New Password Field */}
            <Box>
              <TextField
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                label="New Password"
                variant="outlined"
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        aria-label="toggle password visibility"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <Box sx={{ mt: 1 }}>
                <PasswordStrengthBar
                  onChangeScore={(score) => setPasswordScore(score)}
                  password={newPassword}
                  scoreWords={[
                    "Very Weak",
                    "Weak",
                    "Good",
                    "Strong",
                    "Very Strong",
                  ]}
                  shortScoreWord="Too Short"
                  barColors={[
                    theme.palette.error.main,
                    theme.palette.warning.main,
                    theme.palette.info.main,
                    theme.palette.success.main,
                    theme.palette.success.dark,
                  ]}
                />
                {newPassword.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Chip
                      label={
                        passwordScore >= 4
                          ? "Very Strong"
                          : passwordScore >= 3
                            ? "Strong"
                            : passwordScore >= 2
                              ? "Good"
                              : passwordScore >= 1
                                ? "Weak"
                                : "Very Weak"
                      }
                      size="small"
                      color={
                        passwordScore >= 4
                          ? "success"
                          : passwordScore >= 3
                            ? "warning"
                            : passwordScore >= 2
                              ? "info"
                              : "error"
                      }
                      icon={passwordScore >= 2 ? <CheckCircle /> : <Error />}
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {passwordScore >= 2
                        ? "Password meets requirements"
                        : "Password needs to be at least 'Good' strength"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Confirm Password Field */}
            <TextField
              fullWidth
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              label="Confirm New Password"
              variant="outlined"
              type={showConfirmNewPassword ? "text" : "password"}
              autoComplete="new-password"
              error={
                confirmNewPassword.length > 0 &&
                newPassword !== confirmNewPassword
              }
              helperText={
                confirmNewPassword.length > 0 &&
                newPassword !== confirmNewPassword
                  ? "Passwords do not match"
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }
                      edge="end"
                      aria-label="toggle confirm password visibility"
                    >
                      {showConfirmNewPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {/* Password Requirements */}
            <Alert
              severity="info"
              icon={<Security />}
              sx={{
                borderRadius: 2,
                "& .MuiAlert-message": {
                  fontSize: "0.9rem",
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Password Requirements:
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  • Password strength must be at least "Good"
                </Typography>
                <Typography variant="body2">
                  • Minimum 6 characters with letters, numbers, and special
                  characters
                </Typography>
                <Typography variant="body2">• Passwords must match</Typography>
              </Stack>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={
              changePasswordLoading ||
              !newPassword ||
              !confirmNewPassword ||
              passwordScore < 2 ||
              newPassword !== confirmNewPassword
            }
            startIcon={<LockReset />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              py: 1,
              px: 3,
            }}
          >
            {changePasswordLoading ? "Changing..." : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notify
        open={notifyOpen}
        setOpen={setNotifyOpen}
        message={message}
        severity={severity}
      />
    </Box>
  );
};

export default Login;
